import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { collection, getDocs, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  Avatar,
  Chip,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  AppBar,
  Toolbar,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";
import LoopIcon from "@mui/icons-material/Loop";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleIcon from "@mui/icons-material/People";
import GroupsIcon from "@mui/icons-material/Groups";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import LinkIcon from "@mui/icons-material/Link";
import { ref, deleteObject } from "firebase/storage";
import { storage } from "../firebaseConfig";

function GroupView({ groups, students, teachers, setTeachers, setGroups, setStudents }) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredGroups, setFilteredGroups] = useState(groups);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [studentListModalOpen, setStudentListModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contentModalOpen, setContentModalOpen] = useState(false);
  const [contentGroup, setContentGroup] = useState(null);
  const [contentCategories, setContentCategories] = useState([]);
  const [contentOpenCategories, setContentOpenCategories] = useState({});
  const [contentAllCategoriesOpen, setContentAllCategoriesOpen] = useState(false);
  const [contentSelectedTab, setContentSelectedTab] = useState("lectures");
  const [contentLoading, setContentLoading] = useState(false);
  const [contentError, setContentError] = useState("");

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    try {
      const studentsSnapshot = await getDocs(collection(db, "students"));
      setStudents(
        studentsSnapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }))
      );

      const teachersSnapshot = await getDocs(collection(db, "teachers"));
      setTeachers(
        teachersSnapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }))
      );

      const groupsSnapshot = await getDocs(collection(db, "groups"));
      setGroups(
        groupsSnapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }))
      );
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Sync filteredGroups with groups when groups update
  useEffect(() => {
    setFilteredGroups(groups);
  }, [groups]);

  // Initial fetch on mount
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search
  const handleSearch = (event) => {
    const query = (event.target.value || "").toLowerCase();
    setSearchQuery(query);

    setFilteredGroups(
      (groups || []).filter((group) => {
        const groupName = (group?.name || "").toLowerCase();
        const professor = (teachers || []).find((t) => t.id === group.teacherId);
        const pf = (professor?.firstName || "").toLowerCase();
        const pl = (professor?.lastName || "").toLowerCase();
        return groupName.includes(query) || pf.includes(query) || pl.includes(query);
      })
    );
  };

  // Delete group
  const handleDeleteGroup = async (groupId) => {
    const confirmDeletion = window.confirm(
      t("Are you sure you want to delete this group? This action is irreversible.")
    );
    if (!confirmDeletion) return;

    try {
      await deleteDoc(doc(db, "groups", groupId));

      const updatedStudents = (students || []).map((student) => {
        const updatedEnrollments = (student.enrollments || []).filter(
          (enrollment) => enrollment.groupId !== groupId
        );
        return { ...student, enrollments: updatedEnrollments };
      });
      setStudents(updatedStudents);

      setGroups((prev) => (prev || []).filter((group) => group.id !== groupId));

      const teachersSnapshot = await getDocs(collection(db, "teachers"));
      const teachersToUpdate = teachersSnapshot.docs.filter((teacherDoc) => {
        const teacherData = teacherDoc.data();
        return teacherData.groupIds?.includes(groupId);
      });

      await Promise.all(
        teachersToUpdate.map(async (teacherDoc) => {
          const teacherRef = doc(db, "teachers", teacherDoc.id);
          const updatedGroupIds = (teacherDoc.data().groupIds || []).filter(
            (id) => id !== groupId
          );
          await updateDoc(teacherRef, { groupIds: updatedGroupIds });
        })
      );

      alert(t("Group successfully deleted."));
    } catch (error) {
      console.error("Error deleting group:", error);
      alert(t("An error occurred. Please try again."));
    }
  };

  // View students
  const handleViewStudents = (group) => {
    setSelectedGroup(group);
    setStudentListModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setSelectedGroup(null);
    setStudentListModalOpen(false);
  };

  const handleOpenContentModal = (group) => {
    setContentGroup(group);
    setContentSelectedTab("lectures");
    setContentModalOpen(true);
    fetchGroupFiles(group?.id);
  };

  const handleCloseContentModal = () => {
    setContentModalOpen(false);
    setContentGroup(null);
    setContentCategories([]);
    setContentOpenCategories({});
    setContentAllCategoriesOpen(false);
    setContentError("");
  };

  const fetchGroupFiles = async (groupId) => {
    if (!groupId) return;
    setContentLoading(true);
    setContentError("");
    try {
      const groupRef = doc(db, "groups", groupId);
      const filesSnapshot = await getDocs(collection(groupRef, "files"));

      const fetchedFiles = [];
      filesSnapshot.forEach((docItem) => {
        const fileData = { id: docItem.id, ...docItem.data() };
        if (!fileData.type) fileData.type = "exercises";
        fetchedFiles.push(fileData);
      });

      fetchedFiles.sort((a, b) => (b.uploadedAt || 0) - (a.uploadedAt || 0));

      const grouped = fetchedFiles.reduce((acc, file) => {
        const category = file.category || t("Uncategorized");
        if (!acc[category]) acc[category] = [];
        acc[category].push(file);
        return acc;
      }, {});

      setContentCategories(Object.entries(grouped));
      setContentOpenCategories(
        Object.fromEntries(Object.keys(grouped).map((key) => [key, false]))
      );
      setContentAllCategoriesOpen(false);
    } catch (err) {
      console.error("Error fetching files:", err);
      setContentError(t("An error occurred. Please try again."));
    } finally {
      setContentLoading(false);
    }
  };

  const handleContentTabChange = (event, newValue) => {
    setContentSelectedTab(newValue);
  };

  const handleContentToggleAllCategories = () => {
    setContentAllCategoriesOpen((prev) => {
      const next = !prev;
      setContentOpenCategories((prevOpen) =>
        Object.fromEntries(Object.keys(prevOpen).map((key) => [key, next]))
      );
      return next;
    });
  };

  const handleContentCategoryClick = (category) => {
    setContentOpenCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const filterContentFilesByType = (files) => {
    if (contentSelectedTab === "lectures") {
      return files.filter(
        (file) =>
          file.type === "file" || file.type === "video" || file.type === "link"
      );
    }
    if (contentSelectedTab === "exerciseSheets") {
      return files.filter((file) => file.type === "exercises");
    }
    return files;
  };

  const handleContentFileClick = (file) => {
    if (!file || !file.url) {
      console.error("File URL is not valid");
      return;
    }
    const url = file.url || "";
    const fullUrl =
      url.startsWith("http://") || url.startsWith("https://")
        ? url
        : `https://${url}`;
    window.open(fullUrl, "_blank", "noopener,noreferrer");
  };

  const toggleContentImportant = async (fileId, currentState) => {
    if (!contentGroup?.id || !fileId) return;
    try {
      const groupRef = doc(db, "groups", contentGroup.id);
      const fileRef = doc(collection(groupRef, "files"), fileId);
      await updateDoc(fileRef, { markedImportant: !currentState });
      fetchGroupFiles(contentGroup.id);
    } catch (err) {
      console.error("Error toggling important:", err);
    }
  };

  const deleteContentFile = async (fileId, fileName, fileType) => {
    if (!contentGroup?.id || !fileId) return;
    if (!window.confirm(t("Are you sure you want to delete this file?"))) return;

    try {
      if (fileType !== "link") {
        const storageRef = ref(storage, `groups/${contentGroup.id}/${fileName}`);
        await deleteObject(storageRef);
      }

      const groupRef = doc(db, "groups", contentGroup.id);
      await deleteDoc(doc(collection(groupRef, "files"), fileId));

      fetchGroupFiles(contentGroup.id);
    } catch (error) {
      console.error("Error deleting file:", error);
    }
  };

  const getTeacherName = (teacher) =>
    `${teacher?.firstName || ""} ${teacher?.lastName || ""}`.trim() || t("Unknown");

  const getGroupTitle = (group) =>
    `${group?.name || t("Unnamed group")} (${group?.level || t("Not specified")} - ${group?.subjectName || t("No subject")})`;

  // Count students per group (same logic as your original)
  const groupStudentCount = useMemo(() => {
    const map = new Map();
    (groups || []).forEach((g) => map.set(g.id, 0));

    (students || []).forEach((student) => {
      const enrollments = student?.enrollments;
      if (!Array.isArray(enrollments)) return;

      enrollments.forEach((en) => {
        if (!en?.groupId) return;
        if (!map.has(en.groupId)) map.set(en.groupId, 0);
        map.set(en.groupId, (map.get(en.groupId) || 0) + 1);
      });
    });

    return map;
  }, [groups, students]);

  const modalStudents = useMemo(() => {
    if (!selectedGroup) return [];
    return (students || []).filter(
      (student) =>
        Array.isArray(student.enrollments) &&
        student.enrollments.some((enrollment) => enrollment.groupId === selectedGroup.id)
    );
  }, [students, selectedGroup]);

  return (
    <Box
      sx={{
        direction: "rtl",
        backgroundColor: "#fff",
        borderRadius: 3,
        border: "1px solid rgba(0,0,0,0.06)",
        boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
        overflow: "hidden",
      }}
    >
      {/* Header (matches TeacherView container style) */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          gap: 2,
        }}
      >
        {/* RIGHT: Title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: "#e0f2fe",
              color: "#0369a1",
              width: 46,
              height: 46,
              boxShadow: "0 8px 18px rgba(3,105,161,0.14)",
            }}
          >
            <GroupsIcon />
          </Avatar>

          <Box sx={{ textAlign: "right" }}>
            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
              {t("Groups and classes")}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              {t("View and manage all classes in the platform")}
            </Typography>
          </Box>
        </Box>

        {/* LEFT: Actions + Search */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, direction: "ltr", flexWrap: "wrap" }}>
          <IconButton
            onClick={fetchData}
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              border: "1px solid rgba(0,0,0,0.12)",
              backgroundColor: "#fff",
              "&:hover": { backgroundColor: "#f9fafb" },
            }}
          >
            <LoopIcon sx={{ fontSize: 22, color: "rgba(0,0,0,0.65)" }} />
          </IconButton>

          <TextField
            size="small"
            value={searchQuery}
            onChange={handleSearch}
            placeholder={t("Search by group name or teacher...")}
            sx={{
              width: { xs: "100%", sm: 520 },
              backgroundColor: "#f9fafb",
              borderRadius: 2,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 0 }}>
        <Table sx={{ direction: "rtl" }}>
          <TableHead sx={{ bgcolor: "#ffffff" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>{t("Group name")}</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>{t("Teacher")}</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>{t("Students count")}</TableCell>
              <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>{t("Actions")}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={26} />
                  <Typography sx={{ mt: 1, color: "text.secondary" }}>
                   {t("Loading groups...")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (filteredGroups || []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                 {t("No groups match your search.")}
                </TableCell>
              </TableRow>
            ) : (
              (filteredGroups || [])
                .slice(0, searchQuery ? filteredGroups.length : 7)
                .map((group) => {
                  const professor = (teachers || []).find((t) => t.id === group.teacherId);
                  const enrolledStudentsCount = groupStudentCount.get(group.id) || 0;

                  return (
                    <TableRow
                      key={group.id}
                      hover
                      sx={{
                        "& td": { py: 2 },
                        "&:hover": { backgroundColor: "#fafafa" },
                      }}
                    >
                      {/* Group */}
                      <TableCell sx={{ textAlign: "right" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 38,
                              height: 38,
                              bgcolor: "rgba(3,105,161,0.10)",
                              color: "#0369a1",
                              fontWeight: 900,
                            }}
                          >
                            {(group?.name?.[0] || "ق").toUpperCase()}
                          </Avatar>

                          <Box>
                            <Typography sx={{ fontWeight: 900 }}>
                              {group?.name || t("Unnamed group")}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              {(group?.level || t("Not specified"))} · {(group?.subjectName || t("No subject"))}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Teacher */}
                      <TableCell sx={{ textAlign: "right" }}>
                        <Chip
                          label={getTeacherName(professor)}
                          size="small"
                          sx={{
                            bgcolor: "rgba(99,102,241,0.10)",
                            color: "#4f46e5",
                            fontWeight: 800,
                            borderRadius: "5px",
                          }}
                        />
                      </TableCell>

                      {/* Students count */}
                      <TableCell sx={{ textAlign: "right" }}>
                        <Chip
                          label={`${enrolledStudentsCount} ${t("students")}`}
                          size="small"
                          sx={{
                            bgcolor:
                              enrolledStudentsCount > 0
                                ? "rgba(34,197,94,0.12)"
                                : "rgba(148,163,184,0.18)",
                            color:
                              enrolledStudentsCount > 0
                                ? "rgba(22,163,74,0.95)"
                                : "rgba(100,116,139,0.95)",
                            fontWeight: 800,
                            borderRadius: "5px",
                          }}
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableCell sx={{ textAlign: "right" }}>
                        <Box sx={{ display: "flex", flexDirection: "row-reverse", gap: 1 }}>
                          <IconButton
                            onClick={() => handleDeleteGroup(group.id)}
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              border: "1px solid rgba(0,0,0,0.10)",
                              backgroundColor: "#fff",
                              "&:hover": { backgroundColor: "#f9fafb" },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>

                          <IconButton
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              border: "1px solid rgba(0,0,0,0.10)",
                              backgroundColor: "#fff",
                              "&:hover": { backgroundColor: "#f9fafb" },
                            }}
                            onClick={() => handleOpenContentModal(group)}
                          >
                            <AttachFileIcon />
                          </IconButton>

                          <IconButton
                            onClick={() => handleViewStudents(group)}
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              border: "1px solid rgba(0,0,0,0.10)",
                              backgroundColor: "#fff",
                              "&:hover": { backgroundColor: "#f9fafb" },
                            }}
                          >
                            <PeopleIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Modal: student list */}
      <Dialog
        open={studentListModalOpen}
        onClose={handleCloseModal}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            width: 760,
            maxWidth: "95vw",
            direction: "rtl",
            maxHeight: "90vh",
            overflow: "hidden",
          },
        }}
      >
        <AppBar
          position="relative"
          color="default"
          elevation={0}
          sx={{ borderBottom: "1px solid rgba(0,0,0,0.08)" }}
        >
          <Toolbar sx={{ gap: 1 }}>
            <Typography sx={{ flexGrow: 1, fontWeight: 900, textAlign: "center" }}>
              {t("Enrolled students")}
            </Typography>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <DialogContent sx={{ p: 2.5 }}>
          {selectedGroup ? (
            <>
              {/* Group summary */}
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid rgba(0,0,0,0.08)",
                  backgroundColor: "#fafafa",
                }}
              >
                <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                  {getGroupTitle(selectedGroup)}
                </Typography>
                <Typography sx={{ color: "text.secondary" }}>
                 {t("Students count")}: {modalStudents.length}
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {modalStudents.length > 0 ? (
                <Box
                  sx={{
                    border: "1px solid rgba(0,0,0,0.08)",
                    borderRadius: 2,
                    overflow: "hidden",
                    backgroundColor: "#fff",
                  }}
                >
                  <List sx={{ py: 0, direction: "rtl", textAlign: "right" }}>
                    {modalStudents.map((student) => (
                      <ListItem
                        key={student.id}
                        sx={{
                          py: 1.5,
                          borderBottom: "1px solid rgba(0,0,0,0.06)",
                          "&:last-child": { borderBottom: "none" },
                        }}
                      >
                      <ListItemText
                        sx={{ textAlign: "right" }}
                        primary={
                          <Typography sx={{ fontWeight: 900, textAlign: "right" }}>
                            {student.firstName} {student.lastName}
                          </Typography>
                        }
                        secondary={
                          <Typography sx={{ color: "text.secondary", textAlign: "right" }}>
                            {student.email || "-"}
                          </Typography>
                        }
                      />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ) : (
                <Typography sx={{ display: "flex", justifyContent: "center", color: "text.secondary", py: 4 }}>
                  {t("No students found")}
                </Typography>
              )}
            </>
          ) : (
            <Typography sx={{ color: "text.secondary" }}>{t("Please select a group.")}</Typography>
          )}

          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button onClick={handleCloseModal} variant="outlined" color="error" sx={{ borderRadius: 2 }}>
              {t("Close")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Modal: class content (matches teacher view UI) */}
      <Dialog
        open={contentModalOpen}
        onClose={handleCloseContentModal}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: "92vh",
            overflow: "hidden",
            direction: "rtl",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: 800,
            borderBottom: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <Typography sx={{ fontWeight: 800 }}>
            {contentGroup?.name || t("Class content")}
          </Typography>
          <IconButton onClick={handleCloseContentModal}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ mb: 2 }}>
            <Tabs
              value={contentSelectedTab}
              onChange={handleContentTabChange}
              centered
              indicatorColor="primary"
              TabIndicatorProps={{ sx: { bgcolor: "#0f172a", height: 3, borderRadius: 2 } }}
            >
              <Tab
                value="lectures"
                label={t("Program")}
                sx={{ fontSize: 18, color: "#0f172a", fontWeight: 600, textTransform: "none" }}
              />
              <Tab
                value="exerciseSheets"
                label={t(" المواضيع")}
                sx={{ fontSize: 18, color: "#0f172a", fontWeight: 600, textTransform: "none" }}
              />
            </Tabs>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              mb: 2,
              direction: "ltr",
            }}
          >
            <Button
              variant="contained"
              onClick={handleContentToggleAllCategories}
              sx={{
                fontSize: "18px",
                color: "#fff",
                backgroundColor: "#0f172a",
                border: "1px solid #0f172a",
                "&:hover": { backgroundColor: "#1f2937" },
              }}
            >
              {contentAllCategoriesOpen ? t("إغلاق الكل") : t("فتح الكل")}
            </Button>
          </Box>

          {contentLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : contentError ? (
            <Typography sx={{ textAlign: "center", color: "text.secondary", py: 4 }}>
              {contentError}
            </Typography>
          ) : contentCategories.length === 0 ? (
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                direction: "rtl",
                mt: 6,
                color: "#64748b",
              }}
            >
              {t("لم يتم تحميل أي ملفات حتى الآن")}
            </Typography>
          ) : (
            contentCategories.map(([category, files]) => (
              <Box
                key={category}
                sx={{
                  backgroundColor: "#fff",
                  border: "1px solid #eef1f6",
                  borderRadius: "12px",
                  mb: 2,
                  direction: "rtl",
                  boxShadow: "0 8px 20px rgba(99, 100, 103, 0.05)",
                }}
              >
                <Typography
                  variant="h6"
                  onClick={() => handleContentCategoryClick(category)}
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    padding: "10px",
                    borderRadius: "8px 8px 0 0",
                    textAlign: "right",
                    fontWeight: 700,
                    color: "#0f172a",
                  }}
                >
                  <ArrowCircleLeftIcon
                    sx={{
                      fontSize: "40px",
                      marginLeft: "7px",
                      transform: contentOpenCategories[category]
                        ? "rotate(-90deg)"
                        : "rotate(0)",
                      transition: "transform 0.2s",
                      color: "#0f172a",
                    }}
                  />

                  <b> {category} </b>
                </Typography>

                {contentOpenCategories[category] && (
                  <Box
                    sx={{
                      padding: "10px",
                      backgroundColor: "#fff",
                      borderRadius: "0 0 8px 8px",
                    }}
                  >
                    {filterContentFilesByType(files).map((file) => (
                      <Box
                        key={file.id}
                        sx={{
                          margin: "10px 0",
                          padding: "8px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          direction: "rtl",
                          borderRadius: "10px",
                          backgroundColor: "#f8fafc",
                          border: "1px solid #eef1f6",
                        }}
                      >
                        {file.type === "file" ? (
                          <FilePresentIcon sx={{ fontSize: "30px" }} />
                        ) : file.type === "video" ? (
                          <OndemandVideoIcon sx={{ fontSize: "30px" }} />
                        ) : file.type === "link" ? (
                          <LinkIcon sx={{ fontSize: "30px" }} />
                        ) : null}

                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: "16px",
                            cursor: "pointer",
                            flexGrow: 1,
                            marginRight: "10px",
                            color: "#1d4ed8",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          onClick={() => handleContentFileClick(file)}
                          title={file.name}
                        >
                          {file.name}
                          <Divider sx={{ marginTop: "7px" }} />
                        </Typography>

                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <IconButton onClick={() => toggleContentImportant(file.id, file.markedImportant)}>
                            {file.markedImportant ? <StarIcon color="warning" /> : <StarBorderIcon />}
                          </IconButton>

                          <IconButton
                            onClick={() => deleteContentFile(file.id, file.name, file.type)}
                          >
                            <DeleteIcon color="error" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ))
          )}
        </DialogContent>
      </Dialog>

    </Box>
  );
}

export default GroupView;
